/**
 * Phase 126.I — Classroom Support Hub: Autism reference page.
 *
 * Public, no-login, shareable URL. Strengths-first; classroom strategy,
 * not diagnosis. Identity-affirming language ("autistic students") —
 * no functioning labels, no "disorder" framing. Supports are framed as
 * access and respect, never as making a student appear "typical".
 */
import ProfilePageLayout from "../../components/classroom-support/ProfilePageLayout";

export default function Autism() {
  return (
    <ProfilePageLayout
      slug="autism"
      metaTitle="Autism in the Classroom — Atomic Edu"
      metaDescription="What it looks like, the strengths autistic students bring, and what helps. A teacher-to-teacher guide — strengths-first, identity-affirming, classroom strategy only. No diagnosis, no labels."
      hero={{
        title: "Autism in the Classroom",
        tagline:
          "A trusted colleague's take on what autism can look like in your room, the strengths these students bring, and the moves that work. Identity-affirming. No diagnosis, no functioning labels — classroom strategy only.",
      }}
      sections={[
        {
          id: "looks_like",
          body:
            "Every autistic student is different, but here's the texture you might notice across a week — bright, frequent, sometimes contradictory:",
          bullets: [
            "A strong need for routine and predictability — and distress when plans change without warning.",
            "Deep, focused interests the student returns to with intensity and expertise beyond their years.",
            "Literal interpretation of language — idioms, sarcasm, and 'you know what I mean' shortcuts can land sideways.",
            "Sensory sensitivities — sound, light, texture, movement — sometimes over-responsive, sometimes under-responsive.",
            "Differences in eye contact or back-and-forth conversation that aren't about disengagement.",
            "A need for processing time — answers come a beat or two after you'd expect.",
            "Executive-function differences — planning, holding multi-step information, switching between tasks.",
            "Self-regulating movement (stimming) — flapping, rocking, repeating a phrase — used to stay regulated.",
            "Sometimes masking — hiding how much effort regulation is taking, especially by mid-afternoon.",
          ],
        },
        {
          id: "affects",
          body:
            "Where the friction usually shows up — honest, without making it the whole story:",
          bullets: [
            "Ambiguous or multi-step verbal instructions held in mind across the room.",
            "Unstructured times — transitions, group work, recess — where the rules aren't visible.",
            "Sensory overload that reduces availability to learn even when the content is well within reach.",
            "The social demands of collaborative tasks, especially when the social rules are implicit.",
            "Unexpected schedule changes — the cancelled assembly, the surprise fire drill.",
            "Abstract or figurative language used as the main path into a concept.",
            "Showing what they know, or asking for help, even when the understanding is there. Change or sensory overload can surface as anxiety, shutdown, or a meltdown.",
          ],
        },
        {
          id: "strengths",
          body:
            "The reframe matters. The same brain that finds the unpredictable hard also brings:",
          bullets: [
            "Deep knowledge and focus in areas of interest — often beyond grade level.",
            "Strong memory for facts, sequences, systems, and details.",
            "Attention to detail and accuracy — the student who catches what the rest of the class missed.",
            "Strong visual and spatial thinking.",
            "Pattern recognition and logical reasoning.",
            "Honesty and directness — a trustworthy collaborator.",
            "Perseverance in areas of interest that other students rarely match.",
            "Original perspectives — angles no one else in the room would have offered.",
            "A strong sense of fairness that lifts the classroom culture.",
          ],
        },
        {
          id: "thrives",
          bullets: [
            "Predictability and clear structure — the day visible, the steps named.",
            "Visual supports and explicit expectations — what 'done' looks like, posted.",
            "Advance warning of change — the schedule shift signalled before the moment.",
            "A sensory-considered environment — lighting, noise, proximity all factored in.",
            "Processing time — silence after a question is thinking, not refusal.",
            "Instructions with little ambiguity — literal, concrete, broken into steps.",
            "Their interests used as a bridge into new learning, not redirected away from.",
            "Being explicitly taught social, communication, and executive-function skills — modelled, named, practised.",
            "Patient, understanding educators and peers.",
            "Acceptance — supports framed as access and respect, not as making them appear 'typical'.",
          ],
        },
        {
          id: "strategies",
          body:
            "The moves that show up across IEPs, learning support plans, and the best teachers we've worked with — visual schedules and predictable routines, advance warning of change, literal explicit language, sensory-aware environment, processing time with a written backup to verbal, explicit expectations and clear success criteria, choice in how to show mastery, interest-based entry points, and respecting the regulating movement the student already uses. The generator below assembles these into a plan for the situation you type in.",
        },
      ]}
      ctas={undefined}
      generators={[
        {
          to: "/classroom-support/autism/generator",
          title: "Autism support plan",
          subtitle:
            "Type the situation (e.g., 'Grade 4, autistic student who melts down at unexpected schedule changes') and get a plan you can use tomorrow.",
          cta: "Generate a plan",
        },
      ]}
      readNext={[
        {
          to: "/classroom-support/adhd",
          title: "ADHD in the Classroom",
          subtitle:
            "Movement, novelty, chunks, immediate feedback — the strengths these students bring and what helps when the friction shows up.",
          cta: "Read the guide",
        },
        {
          to: "/classroom-support/2e",
          title: "Twice-Exceptional (2e)",
          subtitle:
            "When giftedness shows up alongside ADHD or another exceptionality, both can hide from teachers for years.",
          cta: "Read the guide",
        },
      ]}
    />
  );
}
